import {
  Node,
  mergeAttributes
} from "@tiptap/core";

import {
  ReactNodeViewRenderer
} from "@tiptap/react";

import RichTextShapeView from
  "./RichTextShapeView";


const SHAPE_TYPES = [
  "rectangle",
  "circle",
  "oval",
  "arrow"
];

const SHAPE_ALIGNMENTS = [
  "left",
  "center",
  "right"
];


const normalizeChoice = (
  value,
  allowedValues,
  fallback
) => {

  return allowedValues.includes(value)
    ? value
    : fallback;

};


const normalizeColor = (
  value,
  fallback
) => {

  return /^#[0-9a-f]{6}$/i.test(
    value || ""
  )
    ? value
    : fallback;

};


const normalizeNumber = (
  value,
  minimum,
  maximum,
  fallback
) => {

  const parsedValue =
    Number(value);

  if (
    !Number.isFinite(parsedValue) ||
    parsedValue < minimum ||
    parsedValue > maximum
  ) {
    return fallback;
  }

  return parsedValue;

};

const normalizeOptionalNumber = (
  value,
  minimum,
  maximum
) => {

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const parsedValue =
    Number(value);

  if (
    !Number.isFinite(parsedValue) ||
    parsedValue < minimum ||
    parsedValue > maximum
  ) {
    return null;
  }

  return parsedValue;

};

const hexToRgba = (
  color,
  opacity
) => {

  const normalizedColor =
    normalizeColor(
      color,
      "#ffffff"
    );

  const normalizedOpacity =
    normalizeNumber(
      opacity,
      0,
      100,
      100
    );

  const red =
    parseInt(
      normalizedColor.slice(1, 3),
      16
    );

  const green =
    parseInt(
      normalizedColor.slice(3, 5),
      16
    );

  const blue =
    parseInt(
      normalizedColor.slice(5, 7),
      16
    );

  return `rgba(${red}, ${green}, ${blue}, ${
    normalizedOpacity / 100
  })`;

};


const RichTextShape =
  Node.create({

    name: "richTextShape",

    group: "block",

    content: "paragraph+",

    defining: true,

    isolating: true,

    selectable: true,

    draggable: false,


    addAttributes() {

      return {

        shapeType: {
          default: "rectangle",

          parseHTML: (element) =>
            normalizeChoice(
              element.getAttribute(
                "data-shape-type"
              ),
              SHAPE_TYPES,
              "rectangle"
            ),

          renderHTML: (attributes) => ({
            "data-shape-type":
              normalizeChoice(
                attributes.shapeType,
                SHAPE_TYPES,
                "rectangle"
              )
          })
        },


        fillColor: {
          default: "#f3f4f6",

          parseHTML: (element) =>
            normalizeColor(
              element.getAttribute(
                "data-fill-color"
              ),
              "#f3f4f6"
            ),

          renderHTML: (attributes) => ({
            "data-fill-color":
              normalizeColor(
                attributes.fillColor,
                "#f3f4f6"
              )
          })
        },


        strokeColor: {
          default: "#000000",

          parseHTML: (element) =>
            normalizeColor(
              element.getAttribute(
                "data-stroke-color"
              ),
              "#000000"
            ),

          renderHTML: (attributes) => ({
            "data-stroke-color":
              normalizeColor(
                attributes.strokeColor,
                "#000000"
              )
          })
        },


        strokeWidth: {
          default: 2,

          parseHTML: (element) =>
            normalizeNumber(
              element.getAttribute(
                "data-stroke-width"
              ),
              1,
              5,
              2
            ),

          renderHTML: (attributes) => ({
            "data-stroke-width":
              String(
                normalizeNumber(
                  attributes.strokeWidth,
                  1,
                  5,
                  2
                )
              )
          })
        },


        fillOpacity: {
          default: 100,

          parseHTML: (element) =>
            normalizeNumber(
              element.getAttribute(
                "data-fill-opacity"
              ),
              0,
              100,
              100
            ),

          renderHTML: (attributes) => ({
            "data-fill-opacity":
              String(
                normalizeNumber(
                  attributes.fillOpacity,
                  0,
                  100,
                  100
                )
              )
          })
        },


        strokeOpacity: {
          default: 100,

          parseHTML: (element) =>
            normalizeNumber(
              element.getAttribute(
                "data-stroke-opacity"
              ),
              0,
              100,
              100
            ),

          renderHTML: (attributes) => ({
            "data-stroke-opacity":
              String(
                normalizeNumber(
                  attributes.strokeOpacity,
                  0,
                  100,
                  100
                )
              )
          })
        },

        width: {
          default: null,

          parseHTML: (element) =>
            normalizeOptionalNumber(
              element.getAttribute(
                "data-shape-width"
              ),
              8,
              1000
            ),

          renderHTML: (attributes) => {

            const width =
              normalizeOptionalNumber(
                attributes.width,
                8,
                1000
              );

            return width === null
              ? {}
              : {
                  "data-shape-width":
                    String(width)
                };

          }
        },


        height: {
          default: null,

          parseHTML: (element) =>
            normalizeOptionalNumber(
              element.getAttribute(
                "data-shape-height"
              ),
              8,
              800
            ),

          renderHTML: (attributes) => {

            const height =
              normalizeOptionalNumber(
                attributes.height,
                8,
                800
              );

            return height === null
              ? {}
              : {
                  "data-shape-height":
                    String(height)
                };

          }
        },


                referenceWidth: {
          default: null,

          parseHTML: (element) =>
            normalizeOptionalNumber(
              element.getAttribute(
                "data-shape-reference-width"
              ),
              100,
              5000
            ),

          renderHTML: (attributes) => {

            const referenceWidth =
              normalizeOptionalNumber(
                attributes.referenceWidth,
                100,
                5000
              );

            return referenceWidth === null
              ? {}
              : {
                  "data-shape-reference-width":
                    String(referenceWidth)
                };

          }
        },


                anchorImageIndex: {
          default: null,

          parseHTML: (element) => {

            const parsedIndex =
              normalizeOptionalNumber(
                element.getAttribute(
                  "data-shape-anchor-image-index"
                ),
                0,
                1000
              );

            return parsedIndex === null
              ? null
              : Math.round(parsedIndex);

          },

          renderHTML: (attributes) => {

            const parsedIndex =
              normalizeOptionalNumber(
                attributes.anchorImageIndex,
                0,
                1000
              );

            return parsedIndex === null
              ? {}
              : {
                  "data-shape-anchor-image-index":
                    String(
                      Math.round(
                        parsedIndex
                      )
                    )
                };

          }
        },


        anchorCenterYRatio: {
          default: null,

          parseHTML: (element) =>
            normalizeOptionalNumber(
              element.getAttribute(
                "data-shape-anchor-center-y-ratio"
              ),
              -10,
              10
            ),

          renderHTML: (attributes) => {

            const ratio =
              normalizeOptionalNumber(
                attributes
                  .anchorCenterYRatio,
                -10,
                10
              );

            return ratio === null
              ? {}
              : {
                  "data-shape-anchor-center-y-ratio":
                    String(ratio)
                };

          }
        },


        offsetX: {
          default: 0,

          parseHTML: (element) =>
            normalizeNumber(
              element.getAttribute(
                "data-shape-offset-x"
              ),
              -2000,
              2000,
              0
            ),

          renderHTML: (attributes) => ({
            "data-shape-offset-x":
              String(
                normalizeNumber(
                  attributes.offsetX,
                  -2000,
                  2000,
                  0
                )
              )
          })
        },


        offsetY: {
          default: 0,

          parseHTML: (element) =>
            normalizeNumber(
              element.getAttribute(
                "data-shape-offset-y"
              ),
              -2000,
              2000,
              0
            ),

          renderHTML: (attributes) => ({
            "data-shape-offset-y":
              String(
                normalizeNumber(
                  attributes.offsetY,
                  -2000,
                  2000,
                  0
                )
              )
          })
        },


        rotation: {
          default: 0,

          parseHTML: (element) =>
            normalizeNumber(
              element.getAttribute(
                "data-shape-rotation"
              ),
              -180,
              180,
              0
            ),

          renderHTML: (attributes) => ({
            "data-shape-rotation":
              String(
                normalizeNumber(
                  attributes.rotation,
                  -180,
                  180,
                  0
                )
              )
          })
        },


        alignment: {
          default: "center",

          parseHTML: (element) =>
            normalizeChoice(
              element.getAttribute(
                "data-shape-alignment"
              ),
              SHAPE_ALIGNMENTS,
              "center"
            ),

          renderHTML: (attributes) => ({
            "data-shape-alignment":
              normalizeChoice(
                attributes.alignment,
                SHAPE_ALIGNMENTS,
                "center"
              )
          })
        }

      };

    },


    parseHTML() {

      return [
        {
          tag:
            'div[data-rich-text-shape="true"]',

          contentElement:
            ".rich-text-shape-text"
        }
      ];

    },


    renderHTML({
      node,
      HTMLAttributes
    }) {

      const {
        shapeType,
        fillColor,
        strokeColor,
        strokeWidth,
        fillOpacity,
        strokeOpacity,
        width,
        height,
        referenceWidth,
        offsetX,
        offsetY,
        rotation,
        alignment
      } = node.attrs;

      const normalizedShapeType =
        normalizeChoice(
          shapeType,
          SHAPE_TYPES,
          "rectangle"
        );

      const normalizedAlignment =
        normalizeChoice(
          alignment,
          SHAPE_ALIGNMENTS,
          "center"
        );

      const renderedFillColor =
        hexToRgba(
          fillColor,
          fillOpacity
        );

      const renderedStrokeColor =
        hexToRgba(
          strokeColor,
          strokeOpacity
        );

            const normalizedStrokeWidth =
        normalizeNumber(
          strokeWidth,
          1,
          5,
          2
        );

            const defaultDimensions =
        {
          rectangle: {
            width: 360,
            height: 150
          },

          circle: {
            width: 220,
            height: 220
          },

          oval: {
            width: 380,
            height: 190
          },

          arrow: {
            width: 400,
            height: 160
          }
        }[normalizedShapeType] ||
        {
          width: 360,
          height: 150
        };

      const normalizedWidth =
        normalizeOptionalNumber(
          width,
          8,
          1000
        ) ??
        defaultDimensions.width;

      const normalizedHeight =
        normalizeOptionalNumber(
          height,
          8,
          800
        ) ??
        defaultDimensions.height;

              const publishedArrowWidth =
        normalizedWidth ?? 400;

      const publishedArrowHeight =
        normalizedHeight ?? 160;

      const publishedArrowVerticalPadding =
        Math.min(
          40,
          publishedArrowHeight * 0.25
        );

      const publishedArrowRightPadding =
        Math.min(
          100,
          publishedArrowWidth * 0.25
        );

      const publishedArrowLeftPadding =
        Math.min(
          42,
          publishedArrowWidth * 0.105
        );

      const publishedArrowSvg =
        normalizedShapeType === "arrow"
          ? [
              '<svg xmlns="http://www.w3.org/2000/svg" ',
              'viewBox="-5 -5 410 170" ',
              'preserveAspectRatio="none">',
              '<polygon ',
              'points="0,40 260,40 260,0 400,80 ',
              '260,160 260,120 0,120" ',
              `fill="${renderedFillColor}" `,
              `stroke="${renderedStrokeColor}" `,
              `stroke-width="${normalizedStrokeWidth}" `,
              'vector-effect="non-scaling-stroke" ',
              'stroke-linejoin="round"/>',
              '</svg>'
            ].join("")
          : "";

      const publishedArrowSurfaceStyle =
        normalizedShapeType === "arrow"
          ? [
              `padding: ${
                publishedArrowVerticalPadding
              }px ${
                publishedArrowRightPadding
              }px ${
                publishedArrowVerticalPadding
              }px ${
                publishedArrowLeftPadding
              }px`,

              "background-color: transparent",

              `background-image: url("data:image/svg+xml,${
                encodeURIComponent(
                  publishedArrowSvg
                )
              }")`,

              "background-position: center",
              "background-repeat: no-repeat",
              "background-size: 100% 100%",
              "clip-path: none",
              "box-sizing: border-box",
              "min-width: 0",
              "min-height: 0"
            ].join("; ")
          : null;

            const normalizedReferenceWidth =
        normalizeOptionalNumber(
          referenceWidth,
          100,
          5000
        );

      const publishedLength = (
        value
      ) =>
        normalizedReferenceWidth === null
          ? `${value}px`
          : `${
              (
                value /
                normalizedReferenceWidth *
                100
              ).toFixed(6)
            }cqw`;

      const normalizedOffsetX =
        normalizeNumber(
          offsetX,
          -2000,
          2000,
          0
        );

      const normalizedOffsetY =
        normalizeNumber(
          offsetY,
          -2000,
          2000,
          0
        );

      const normalizedRotation =
        normalizeNumber(
          rotation,
          -180,
          180,
          0
        );

      const shapeStyles = [
        `--shape-fill-color: ${renderedFillColor}`,
        `--shape-stroke-color: ${renderedStrokeColor}`,
        `--shape-stroke-width: ${normalizedStrokeWidth}px`,
        `--shape-offset-x: ${publishedLength(
          normalizedOffsetX
        )}`,

        `--shape-offset-y: ${publishedLength(
          normalizedOffsetY
        )}`,
        `--shape-flow-margin: ${publishedLength(
          20
        )}`,

        `--shape-leading-gap: ${publishedLength(
          46.3
        )}`,

        `--shape-rotation: ${normalizedRotation}deg`
      ];

      if (normalizedWidth !== null) {

        shapeStyles.push(
            `--shape-width: ${publishedLength(
            normalizedWidth
          )}`
        );

      }

      if (normalizedHeight !== null) {

        shapeStyles.push(
            `--shape-height: ${publishedLength(
            normalizedHeight
          )}`
        );

      }

      const shapeStyle =
        shapeStyles.join("; ");


      return [

        "div",

        mergeAttributes(
          HTMLAttributes,
          {
            "data-rich-text-shape":
              "true",

            class:
              `rich-text-shape-wrapper rich-text-shape-align-${normalizedAlignment}`,

            style:
              shapeStyle
          }
        ),

                [
          "div",

          {
            class:
              [
                "rich-text-shape-surface",

                `rich-text-shape-${normalizedShapeType}`,

                normalizedShapeType ===
                "arrow"
                  ? "rich-text-shape-svg-arrow"
                  : ""
              ]
                .filter(Boolean)
                .join(" "),

            ...(
              publishedArrowSurfaceStyle
                ? {
                    style:
                      publishedArrowSurfaceStyle
                  }
                : {}
            )
          },

          [
            "div",

            {
              class:
                "rich-text-shape-text"
            },

            0
          ]
        ]

      ];

    },


    addNodeView() {

      return ReactNodeViewRenderer(
        RichTextShapeView
      );

    },


    addCommands() {

      return {

        insertRichTextShape:
          (attributes = {}) =>
          ({ commands }) =>

            commands.insertContent({

              type:
                this.name,

              attrs:
                attributes,

              content: [
                {
                  type:
                    "paragraph",

                  content: [
                    {
                      type:
                        "text",

                      text:
                        "Write text here..."
                    }
                  ]
                }
              ]

            })

      };

    }

  });


export default RichTextShape;